import { getSiloV3MarketEntry, type SiloHalfStatic } from '@1delta/data-sdk'
import { formatUnits } from 'viem'
import type { AdditionalYields } from '../../../types'
import type { GenericTokenList } from '../../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import {
  SILO_V3_CALLS_PER_SIDE,
  SILO_V3_CALLS_PER_PAIR,
} from './publicCallBuild'
import {
  parseIRMConfig,
  calculateSiloRates,
} from '../silo-v2/irmRates'

/**
 * Parser for the Silo v3 dynamic-state multicall. Byte-for-byte identical to
 * the v2 parser (`silo-v2/publicCallParse.ts`) aside from the registry
 * lookup and the `SILO_V3` branding — v3 shares the same on-chain data
 * layout and fee/LTV semantics as v2, so every downstream consumer of the
 * reserve payload sees the same shape regardless of version.
 */
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export const getSiloV3ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => { data: any; chainId: string } | undefined, number] => {
  const market = getSiloV3MarketEntry(chainId, lender)
  if (!market) return [() => undefined, 0]

  return [
    (data: any[]) => {
      if (!data || data.length !== SILO_V3_CALLS_PER_PAIR) return undefined

      const out: { [marketUid: string]: any } = {}
      const sides: Array<{ self: SiloHalfStatic; other: SiloHalfStatic }> = [
        { self: market.silo0, other: market.silo1 },
        { self: market.silo1, other: market.silo0 },
      ]

      const toBigInt = (v: any): bigint => {
        if (v === undefined || v === null || v === '0x') return 0n
        if (typeof v === 'bigint') return v
        try {
          return BigInt(v as any)
        } catch {
          return 0n
        }
      }

      for (let s = 0; s < 2; s++) {
        const { self, other } = sides[s]
        const slot = s * SILO_V3_CALLS_PER_SIDE
        const totalAssetsRaw = toBigInt(data[slot])
        const debtAssetsRaw = toBigInt(data[slot + 1])
        const irmConfigRaw = data[slot + 2]

        const decimals = self.decimals
        const totalDeposits = Number(formatUnits(totalAssetsRaw, decimals))
        const totalDebt = Number(formatUnits(debtAssetsRaw, decimals))
        const totalLiquidity = totalDeposits - totalDebt
        const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0

        // Derive rates from the IRM config + current utilization. A null
        // `irmParams` (reverted call / unexpected shape) falls back to 0%.
        let depositRatePct = 0
        let variableBorrowRatePct = 0
        const irmParams = parseIRMConfig(irmConfigRaw, self)
        if (irmParams) {
          try {
            const rates = calculateSiloRates(utilization, irmParams)
            variableBorrowRatePct = rates.borrowRate
            depositRatePct = rates.depositRate
          } catch {
            // keep 0% defaults on compute failure
          }
        }

        const tokenAddrLc = self.token.toLowerCase()
        const assetMeta = tokenList[tokenAddrLc]
        const oracleKey =
          toOracleKey(assetMeta?.assetGroup ?? null) ||
          toGenericPriceKey(tokenAddrLc, chainId)
        const price = prices[oracleKey] ?? 0

        // selfLt > 0 → can use this asset as collateral.
        // otherLt > 0 → counterpart is collateral-usable, so this asset is
        // borrowable. A side with lt == 0 is the borrow-only half of an
        // asymmetric pair.
        const selfLt = Number(self.lt) / 1e18
        const selfMaxLtv = Number(self.maxLtv) / 1e18
        const otherLt = Number(other.lt) / 1e18

        // `liquidationFee` is the seize penalty, WAD-scaled (1e18 = 100%) —
        // same scale as lt/maxLtv. Normalize to a plain fraction.
        const selfLiquidationPenalty = Number(self.liquidationFee ?? 0) / 1e18

        const collateralActive = selfLt > 0
        const borrowingEnabled = otherLt > 0

        const marketUid = createMarketUid(chainId, lender, self.silo)

        out[marketUid] = {
          marketUid,
          name: market.name
            ? `Silo V3 ${market.name} ${assetMeta?.symbol ?? ''}`
            : `Silo V3 ${assetMeta?.symbol ?? ''}`,
          poolId: self.silo.toLowerCase(),
          underlying: tokenAddrLc,
          asset: {
            ...(assetMeta ?? {}),
            chainId,
            decimals,
            address: tokenAddrLc,
            symbol: assetMeta?.symbol ?? self.symbol ?? '',
            name: assetMeta?.name ?? self.symbol ?? '',
          },
          totalDeposits,
          totalDebt,
          totalDebtStable: 0,
          totalLiquidity,
          borrowLiquidity: totalLiquidity,
          totalDepositsUSD: totalDeposits * price,
          totalDebtUSD: totalDebt * price,
          totalDebtStableUSD: 0,
          totalLiquidityUSD: totalLiquidity * price,
          borrowLiquidityUSD: totalLiquidity * price,
          utilization,
          depositRate: depositRatePct,
          variableBorrowRate: variableBorrowRatePct,
          stableBorrowRate: 0,
          intrinsicYield:
            additionalYields?.intrinsicYields?.[assetMeta?.assetGroup as any] ??
            0,
          rewards: undefined,
          decimals,
          config: {
            0: {
              category: 0,
              borrowCollateralFactor: selfMaxLtv,
              collateralFactor: selfLt,
              borrowFactor: 1,
              liquidationPenalty: selfLiquidationPenalty,
              closeFactor: 1,
              collateralDisabled: !collateralActive,
              debtDisabled: !borrowingEnabled,
            },
          },
          closeFactor: 1,
          collateralActive,
          borrowingEnabled,
          depositsEnabled: true,
          hasStable: false,
          isActive: true,
          isFrozen: false,
          params: {
            metadata: {
              silo: self.silo,
              counterpartySilo: other.silo,
              siloConfig: market.siloConfig,
              oracle: self.solvencyOracle || ZERO_ADDRESS,
              irm: self.interestRateModel || ZERO_ADDRESS,
              shareTokens: {
                collateral: self.collateralShareToken,
                protected: self.protectedShareToken,
                debt: self.debtShareToken,
              },
              fees: {
                dao: self.daoFee,
                deployer: self.deployerFee,
                liquidation: self.liquidationFee,
                flashloan: self.flashloanFee,
              },
            },
          },
        }
      }

      return { data: out, chainId }
    },
    SILO_V3_CALLS_PER_PAIR,
  ]
}
